import type { KeyboardEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'

interface SegmentedOption<T extends string> {
    value: T
    label: ReactNode
    ariaLabel?: string
}

interface SegmentedControlProps<T extends string> {
    value: T
    onChange: (value: T) => void
    options: SegmentedOption<T>[]
    label: string
    size?: 'sm' | 'default'
    className?: string
}

// A single choice toggle (radio group) for ranges, metrics and view modes
export const SegmentedControl = <T extends string>({
    value,
    onChange,
    options,
    label,
    size = 'default',
    className
}: SegmentedControlProps<T>): ReactNode => {
    const refs = useRef<(HTMLButtonElement | null)[]>([])

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
        if (step === 0) return

        event.preventDefault()
        const next = (index + step + options.length) % options.length
        onChange(options[next].value)
        refs.current[next]?.focus()
    }

    return (
        <div
            role='radiogroup'
            aria-label={label}
            className={cn('inline-flex items-center gap-0.5 rounded-lg border bg-muted/60 p-0.5', className)}
        >
            {options.map((option, index) => {
                const selected = option.value === value

                return (
                    <button
                        key={option.value}
                        ref={(element) => {
                            refs.current[index] = element
                        }}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        aria-label={option.ariaLabel}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(option.value)}
                        onKeyDown={(event) => onKeyDown(event, index)}
                        className={cn(
                            'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50 [&_svg]:size-4 [&_svg]:shrink-0',
                            size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-sm',
                            selected
                                ? 'bg-card text-foreground shadow-xs ring-1 ring-foreground/8'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {option.label}
                    </button>
                )
            })}
        </div>
    )
}
